import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import axiosInstance from '../services/axiosService';
import { toast } from 'react-toastify';
import Modal from 'react-modal';

const CreateOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state?.order;
  const [firstMount, setFirstMount] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchRef = useRef(null);
  const [oldOrders, setOldOrders] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isSearchingOrder, setIsSearchingOrder] = useState(false);
  const [orderSearchError, setOrderSearchError] = useState('');
  const [formData, setFormData] = useState({
    customer_name: orderData?.name || '',
    customer_phone: orderData?.phone || '',
    customer_address: orderData?.address_note || '',
    location: '',
    distance: '',
    items: [],
    discounts: [],
    note: orderData?.note || '',
    note_system: '',
  });
  // Add states for products and loading
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [shippingFee, setShippingFee] = useState(0);
  const [useCustomShipping, setUseCustomShipping] = useState(false);
  const [customShippingFee, setCustomShippingFee] = useState(0);
  
  // Use useEffect to create allItems only when products change
  const [allItems, setAllItems] = useState([]);
  
  // Update allItems when products change
  useEffect(() => {
    setAllItems(products);
  }, [products]);
  
  // Update the search effect dependency to use the memoized allItems
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = allItems.filter(item => 
      item?.name?.toLowerCase().includes(term) || 
      item?.sku?.toLowerCase().includes(term)
    );
    
    setSearchResults(filtered);
    setHighlightedIndex(0);
  }, [searchTerm, allItems]);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await axiosInstance.get('/api/admin/products');
        
        if (response.data.data && Array.isArray(response.data.data)) {
          setProducts(response.data.data);
        } else {
          // Handle case where data is not in expected format
          console.error('API response is not in expected format:', response.data.data);
          toast.error('Dữ liệu sản phẩm không hợp lệ');
          setProducts([]); // Use empty array as fallback
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        toast.error('Không thể tải danh sách sản phẩm');
        setProducts([]); // Use empty array as fallback
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);
  
  // Calculate shipping fee based on distance
  const calculateShippingFee = (distance) => {
    if (!distance || distance < 4.0) return 0;
    if (distance >= 4.0 && distance < 5.5) return 10000;
    if (distance >= 5.5 && distance < 7.0) return 20000;
    if (distance >= 7.0 && distance < 8.5) return 30000;
    if (distance >= 8.5 && distance < 10.0) return 40000;
    if (distance >= 10.0 && distance < 11.5) return 50000;
    if (distance >= 11.5 && distance < 13.0) return 60000;
    if (distance >= 13.0 && distance < 15.0) return 70000;
    if (distance >= 15.0 && distance < 17.0) return 80000;
    if (distance >= 17.0 && distance <= 20.0) return 90000;
    return null; // Invalid distance (over 20.0 km)
  };

  // Handle outside click to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'distance') {
      const distance = parseFloat(value);
      if (distance > 20.0) {
        toast.error('Không giao hàng cho khoảng cách lớn hơn 20km');
        return;
      }
      
      const fee = calculateShippingFee(distance);
      setShippingFee(fee);
      
      // If not using custom shipping, also update the custom field with the calculated fee
      if (!useCustomShipping) {
        setCustomShippingFee(fee);
      }
    }
    
    setFormData({ ...formData, [name]: value });
  };

  // Add a handler for custom shipping fee changes
  const handleCustomShippingChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setCustomShippingFee(value);
  };

  // Add a toggle function for custom shipping
  const toggleCustomShipping = () => {
    if (!useCustomShipping) {
      // When enabling custom shipping, start with the calculated value
      setCustomShippingFee(shippingFee);
    }
    setUseCustomShipping(!useCustomShipping);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchResults.length > 0) {
        addItemToCart(searchResults[highlightedIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
    }
  };

  const addItemToCart = (item) => {
    if (item.type === 'product') {
      const existingItemIndex = formData.items.findIndex(
        existingItem => existingItem.product_id === item.id
      );

      if (existingItemIndex >= 0) {
        // Product already exists, increase quantity
        const updatedItems = [...formData.items];
        updatedItems[existingItemIndex].quantity += 1;
        setFormData({ ...formData, items: updatedItems });
        toast.info(`Đã tăng số lượng ${item.name} thêm 1`);
      } else {
        // Add new product to cart
        const newItem = {
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: 1,
          sku: item.sku
        };
        setFormData({ 
          ...formData, 
          items: [...formData.items, newItem] 
        });
        toast.success(`Đã thêm ${item.name} vào giỏ hàng`);
      }
    } else if (item.type === 'discount') {
      const existingDiscountIndex = formData.discounts.findIndex(
        discount => discount.discount_id === item.id
      );

      if (existingDiscountIndex >= 0) {
        // Discount already exists, increase quantity
        const updatedDiscounts = [...formData.discounts];
        updatedDiscounts[existingDiscountIndex].quantity += 1;
        setFormData({ ...formData, discounts: updatedDiscounts });
        toast.info(`Đã tăng số lượng chiết khấu ${item.name} thêm 1`);
      } else {
        // Add new discount
        const newDiscount = {
          discount_id: item.id,
          name: item.name,
          value: item.value,
          price: item.price,
          quantity: 1,
          sku: item.sku
        };
        
        setFormData({ 
          ...formData, 
          discounts: [...formData.discounts, newDiscount] 
        });
        
        toast.success(`Đã thêm chiết khấu ${item.name}`);
      }
    }
    
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleQuantityChange = (index, value, type) => {
    if (type === 'product') {
      const updatedItems = [...formData.items];
      updatedItems[index].quantity = parseInt(value);
      setFormData({ ...formData, items: updatedItems });
    } else if (type === 'discount') {
      const updatedDiscounts = [...formData.discounts];
      updatedDiscounts[index].quantity = parseInt(value);
      setFormData({ ...formData, discounts: updatedDiscounts });
    }
  };

  const removeItem = (index) => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    setFormData({ ...formData, items: updatedItems });
  };

  const removeDiscount = (index) => {
    const updatedDiscounts = [...formData.discounts];
    updatedDiscounts.splice(index, 1);
    setFormData({ ...formData, discounts: updatedDiscounts });
    toast.info('Đã xóa chiết khấu');
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  };

  const calculateDiscount = () => {
    // Calculate fixed amount discounts with quantity
    let totalDiscount = formData.discounts.reduce((total, discount) => {
      return total + (discount.value * discount.quantity);
    }, 0);

    // Make sure discount doesn't exceed subtotal
    return Math.min(totalDiscount, calculateSubtotal());
  };

  const [badWeatherFee, setBadWeatherFee] = useState(false);
  const [shippingDiscount, setShippingDiscount] = useState(false);
  const [eatInStore, setEatInStore] = useState(false);

  const defaultEatInStoreData = {
    customer_name: 'Pizzata',
    customer_phone: '0347838689',
    customer_address: 'Pizzata Dương Quảng Hàm, Liên Nghĩa, Văn Giang',
    location: '20.923141039502795, 105.933494155011',
    distance: 0
  };

  useEffect(() => {
    if (firstMount) {
      setFirstMount(false);
      return;
    }

    if (eatInStore) {
      setFormData((prev) => ({
        ...prev,
        ...defaultEatInStoreData
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        location: '',
        distance: ''
      }));
    }
  }, [eatInStore]);

  const getFinalShippingFee = () => {
    let fee = useCustomShipping ? customShippingFee : shippingFee;
    if (badWeatherFee) fee += 10000; // Phí thời tiết xấu +10.000đ
    if (shippingDiscount) fee -= 10000; // Giảm phí vận chuyển -10.000đ
    if (fee < 0) fee = 0;
    return fee;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = calculateDiscount();
    const finalShippingFee = getFinalShippingFee();
    return subtotal - discount + finalShippingFee;
  };

  const getItemDisplayType = (item) => {
    return item.type === 'product' ? 'Sản phẩm' : 'Chiết khấu';
  };

  const getItemDisplayValue = (item) => {
    return `${item.price?.toLocaleString('vi-VN')} đ`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.customer_name || !formData.customer_phone || !formData.customer_address) {
      toast.error('Vui lòng điền đầy đủ thông tin khách hàng');
      return;
    }
    
    if (formData.items.length === 0) {
      toast.error('Vui lòng thêm ít nhất một sản phẩm vào đơn hàng');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const orderData = {
        ...formData,
        bad_weather_fee: badWeatherFee,
        shipping_discount: shippingDiscount,
        shipping_fee: useCustomShipping ? customShippingFee : shippingFee,
        eat_in_store: eatInStore,
        subtotal: calculateSubtotal(),
        discount_amount: calculateDiscount(),
        total_amount: calculateTotal(),
      };

      const response = await axiosInstance.post('api/admin/create-order', orderData);

      if (response.data.status === 'success') {
        toast.success('Tạo đơn hàng thành công!');
        // navigate('/orders');
      } else {
        toast.error('Không thể tạo đơn hàng. Vui lòng thử lại sau.');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Không thể tạo đơn hàng. Vui lòng thử lại sau.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hàm tìm đơn hàng cũ theo số điện thoại
  const handleFindOldOrders = async () => {
    const phone = formData.customer_phone.trim();
    if (!phone) {
      toast.error('Vui lòng nhập số điện thoại trước khi tìm');
      return;
    }
    setIsSearchingOrder(true);
    setOrderSearchError('');
    setOldOrders([]);
    try {
      const res = await axiosInstance.get(`/api/orders?phone=${encodeURIComponent(phone)}`);
      if (res.data.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setOldOrders(res.data.data);
        setShowOrderModal(true);
      } else {
        setOrderSearchError('Số điện thoại chưa có đơn hàng');
        toast.info('Số điện thoại chưa có đơn hàng');
      }
    } catch (err) {
      setOrderSearchError('Không thể tìm đơn hàng');
      toast.error('Không thể tìm đơn hàng');
    } finally {
      setIsSearchingOrder(false);
    }
  };

  // Khi chọn đơn cũ, fill thông tin
  const handleSelectOldOrder = (order) => {
    setFormData(prev => ({
      ...prev,
      customer_name: order.name || '',
      customer_phone: order.phone || '',
      customer_address: order.address_note || '',
      location: order.location || '',
      distance: order.distance || '',
    }));
    
    const fee = calculateShippingFee(order.distance);
    setShippingFee(fee);
    setShowOrderModal(false);
  };

  return (
    <Layout>
      <div className="bg-white shadow rounded-lg p-6">
        {/* Show loading indicator when fetching products */}
        {loadingProducts && (
          <div className="text-center py-4 mb-4">
            <div className="spinner"></div>
            <p className="mt-2 text-gray-500">Đang tải dữ liệu sản phẩm...</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          {/* Customer Information */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Thông tin khách hàng</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tên khách hàng</label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Số điện thoại</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFindOldOrders();
                      }
                    }}
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleFindOldOrders}
                    disabled={isSearchingOrder}
                    className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                  >
                    {isSearchingOrder ? 'Đang tìm...' : 'Tìm'}
                  </button>
                </div>
                {orderSearchError && (
                  <div className="text-red-500 text-xs mt-1">{orderSearchError}</div>
                )}
              </div>
            </div>
            
            {/* Address and Distance on the same row */}
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Địa chỉ</label>
                <input
                  type="text"
                  name="customer_address"
                  value={formData.customer_address}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Vị trí</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Khoảng cách (km)</label>
                <input
                  type="number"
                  name="distance"
                  value={formData.distance}
                  onChange={handleInputChange}
                  min="0"
                  max="20.0"
                  step="0.1"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {formData.distance > 0 && (
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">
                      Phí tính theo khoảng cách: {shippingFee ? `${shippingFee.toLocaleString('vi-VN')} đ` : 'Miễn phí'}
                    </span>
                    {parseFloat(formData.distance) > 20.0 && (
                      <span className="text-sm text-red-600 block">
                        Không giao hàng cho khoảng cách lớn hơn 20km
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Custom Shipping Fee */}
            <div className="mt-3 flex items-center">
              <div className="flex items-center">
                <input
                  id="custom-shipping"
                  type="checkbox"
                  checked={useCustomShipping}
                  onChange={toggleCustomShipping}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="custom-shipping" className="ml-2 text-sm font-medium text-gray-700">
                  Điều chỉnh phí vận chuyển
                </label>
              </div>
            </div>

            {useCustomShipping && (
              <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-4">
                <div className="sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phí vận chuyển (đ)</label>
                  <input
                    type="number"
                    value={customShippingFee}
                    onChange={handleCustomShippingChange}
                    min="0"
                    step="1000"
                    className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="sm:col-span-3 flex items-end">
                  <span className="text-sm text-gray-500 italic mb-2">
                    * Phí vận chuyển đã được điều chỉnh thủ công
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Unified Search */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Tìm kiếm sản phẩm & chiết khấu</h3>
            <div className="relative" ref={searchRef}>
              <div className="flex">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowResults(true);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => searchTerm && setShowResults(true)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tìm kiếm sản phẩm/chiết khấu theo tên hoặc mã SKU..."
                />
              </div>
              
              {showResults && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {searchResults.length > 0 && (
                    <div className="px-4 py-2 bg-gray-100 text-gray-600 text-sm border-b">
                      <span className="font-medium">Nhấn Enter</span> để chọn mục được highlight hoặc dùng ↑↓ để di chuyển
                    </div>
                  )}
                  {searchResults.map((item, index) => (
                    <div 
                      key={item.id}
                      id={`search-result-${index}`}
                      className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center 
                        ${index === highlightedIndex ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                      onClick={() => addItemToCart(item)}
                    >
                      <div>
                        <div className={`font-medium ${index === highlightedIndex ? 'text-blue-700' : ''}`}>
                          {item.name}
                          {index === highlightedIndex && <span className="text-xs ml-2 text-blue-600">(Enter để chọn)</span>}
                        </div>
                        <div className="text-sm flex space-x-2">
                          <span className="text-gray-500">SKU: {item.sku}</span>
                          <span className={item.type === 'discount' ? 'text-green-600' : 'text-blue-600'}>
                            {getItemDisplayType(item)}
                          </span>
                        </div>
                      </div>
                      <div className={`font-medium ${index === highlightedIndex ? 'text-blue-700' : 'text-blue-600'}`}>
                        {getItemDisplayValue(item)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Cart Items */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Giỏ hàng</h3>
            
            {formData.items.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-md border border-dashed border-gray-300">
                <p className="text-gray-500">Chưa có sản phẩm nào trong giỏ hàng</p>
                <p className="text-sm text-gray-400 mt-1">Tìm kiếm và thêm sản phẩm vào giỏ hàng</p>
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mã SKU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sản phẩm
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Đơn giá
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Số lượng
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thành tiền
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 uppercase">{item.sku}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{item.price.toLocaleString('vi-VN')} đ</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(index, e.target.value, 'product')}
                            className="w-16 border rounded-md px-2 py-1 text-sm"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {(item.price * item.quantity).toLocaleString('vi-VN')} đ
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Discounts */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Chiết khấu</h3>
            
            {formData.discounts.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-md border border-dashed border-gray-300">
                <p className="text-gray-500">Chưa có chiết khấu nào được áp dụng</p>
                <p className="text-sm text-gray-400 mt-1">Tìm kiếm và chọn chiết khấu để áp dụng</p>
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mã SKU
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tên chiết khấu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Giá trị
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Số lượng
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thành tiền
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {formData.discounts.map((discount, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 uppercase">{discount.sku}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{discount.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {discount.value.toLocaleString('vi-VN')} đ
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="1"
                            value={discount.quantity}
                            onChange={(e) => handleQuantityChange(index, e.target.value, 'discount')}
                            className="w-16 border rounded-md px-2 py-1 text-sm"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {(discount.value * discount.quantity).toLocaleString('vi-VN')} đ
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => removeDiscount(index)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Order Summary */}
            <div className="flex justify-end mt-4">
              <div className="flex">
                {/* Checkbox block */}
                <div className="flex flex-col justify-end gap-3 mb-2 mr-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={badWeatherFee}
                      onChange={() => setBadWeatherFee(v => !v)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Phí thời tiết xấu</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={shippingDiscount}
                      onChange={() => setShippingDiscount(v => !v)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Giảm phí vận chuyển</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={eatInStore}
                      onChange={() => setEatInStore(v => !v)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Đơn ăn tại quán</span>
                  </label>
                </div>
                {/* Khối tổng kết giữ nguyên */}
                <div className="bg-gray-100 p-4 rounded-md w-72">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Tạm tính:</span>
                    <span className="text-gray-700">{calculateSubtotal().toLocaleString('vi-VN')} đ</span>
                  </div>
                  <div className="flex justify-between mb-2 text-red-600">
                    <span>Chiết khấu:</span>
                    <span>- {calculateDiscount().toLocaleString('vi-VN')} đ</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-700">Phí vận chuyển:</span>
                    <span className={`${useCustomShipping ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                      {getFinalShippingFee().toLocaleString('vi-VN')} đ
                      {useCustomShipping && <span className="text-xs ml-1">(Đã điều chỉnh)</span>}
                    </span>
                  </div>
                  <div className="border-t border-gray-300 my-2 pt-2 flex justify-between">
                    <span className="font-bold">Tổng cộng:</span>
                    <span className="font-bold">{calculateTotal().toLocaleString('vi-VN')} đ</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Additional Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú hệ thống</label>
            <input
              type="text"
              name="note_system"
              value={formData.note_system}
              onChange={handleInputChange}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="VD: c2-p12m, c1-p1s,..."
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Ghi chú</label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleInputChange}
              rows="3"
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            ></textarea>
          </div>
          
          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/orders')}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 mr-3"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 rounded-md text-white ${isSubmitting ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {isSubmitting ? 'Đang xử lý...' : 'Tạo đơn hàng'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal chọn đơn hàng cũ */}
      <Modal
        isOpen={showOrderModal}
        onRequestClose={() => setShowOrderModal(false)}
        ariaHideApp={false}
        className="fixed inset-0 flex items-center justify-center z-50"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 z-40"
      >
        <div className="relative bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
          <h2 className="text-lg font-bold mb-4">Chọn đơn hàng cũ</h2>
          <button
            className="absolute z-50 top-2 right-4 text-gray-500 hover:text-gray-700 text-xl font-bold"
            onClick={() => setShowOrderModal(false)}
          >
            X
          </button>
          <div className="divide-y">
            {oldOrders.map((order, idx) => (
              <div key={order.id || idx} className="py-3 cursor-pointer hover:bg-blue-50 px-2 rounded flex flex-col gap-1" onClick={() => handleSelectOldOrder(order)}>
                <div className="font-medium">{order.name} - {order.phone}</div>
                <div className="text-sm text-gray-600">Địa chỉ: {order.address_note}</div>
                <div className="text-xs text-gray-400">Vị trí: {order.location} | Khoảng cách: {order.distance} km</div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default CreateOrder; 